import React from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonItem,
  IonIcon,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonSpinner,
  IonText,
  IonToast,
} from '@ionic/react';
import {
  radioOutline,
  batteryHalfOutline,
  cellularOutline,
  codeSlashOutline,
  gitNetworkOutline,
  hardwareChipOutline,
  thermometerOutline,
} from 'ionicons/icons';
import { useAdminDeviceMqtt } from '../../../application/use-cases/admin/useAdminDeviceMqtt';
import { METRIC_LABEL, type SensorMetric } from '../../../domain/Measurement';
import type { ComponentDataStatus, SensorFieldTarget } from '../../../domain/Admin';
import { COMPONENT_ICON } from '../../components/station/ComponentPicker';
import { ConnectivityBadge } from '../../components/ConnectivityStatus';
import { formatRelative } from '../../../domain/WeatherStation';
import DeviceHealthCard from '../../components/admin/DeviceHealthCard';
import './Admin.css';

const SINGLE_METRICS: SensorMetric[] = ['temperature', 'relative_humidity', 'pressure', 'rain', 'wind_speed', 'wind_direction'];

// Opciones del selector de cada campo: "metric:channel"; '' = ignorar. De
// suelo se ofrecen las sondas que ya usa la estación más una nueva: un spot
// sin sondas ve una sola opción, no una lista de sondas que no tiene.
function targetOptions(usedSoilChannels: number[]): { value: string; label: string }[] {
  const used = Array.from(new Set(usedSoilChannels)).sort((a, b) => a - b);
  let next = 1;
  while (used.includes(next)) next++;
  const soil = [...used, next];
  return [
    ...SINGLE_METRICS.map((m) => ({ value: `${m}:1`, label: METRIC_LABEL[m] })),
    ...soil.map((ch) => ({
      value: `soil_moisture:${ch}`,
      label: soil.length === 1 ? METRIC_LABEL.soil_moisture : `${METRIC_LABEL.soil_moisture} (sonda ${ch})`,
    })),
  ];
}

const COMPONENT_STATE: Record<ComponentDataStatus, { text: string; color: string }> = {
  receiving: { text: 'Llegan datos', color: 'success' },
  stale: { text: 'Dejaron de llegar datos (nada en el último día)', color: 'warning' },
  no_data: { text: 'Nunca llegaron datos: falta el sensor en el equipo o mapear su campo', color: 'warning' },
  hidden: { text: 'Llegan datos, pero el usuario lo apagó: no se muestran', color: 'warning' },
  actuator: { text: 'Válvula: recibe órdenes, no manda datos', color: 'medium' },
};

const toValue = (t: SensorFieldTarget | undefined) => (t ? `${t.metric}:${t.channel}` : '');
const fromValue = (v: string): SensorFieldTarget | null => {
  if (!v) return null;
  const [metric, channel] = v.split(':');
  return { metric: metric as SensorMetric, channel: Number(channel) };
};

const AdminDeviceMqtt: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data,
    loading,
    error,
    toast,
    setToast,
    topic,
    setTopic,
    linking,
    handleLink,
    drafts,
    mappingFor,
    setFieldTarget,
    saveMapping,
    savingSensor,
  } = useAdminDeviceMqtt(id);

  const latestHealth = data?.health.latest?.health ?? null;
  const options = targetOptions(
    (data?.sensors ?? [])
      .flatMap((sensor) => Object.values(mappingFor(sensor.id)))
      .filter((t) => t.metric === 'soil_moisture')
      .map((t) => t.channel),
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin/devices" />
          </IonButtons>
          <IonTitle>{data ? data.device.name : 'Origen de datos'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {data && !loading && (
          <>
            <div className="admin-entity-hero">
              <span className="admin-entity-icon">
                <IonIcon icon={radioOutline} />
              </span>
              <div className="admin-entity-body">
                <div className="admin-entity-title">
                  <h2>{data.device.name}</h2>
                  <ConnectivityBadge device={data.device} />
                </div>
                <p>{data.device.lastSeenAt ? `Último mensaje ${formatRelative(data.device.lastSeenAt)}` : 'Nunca mandó nada'}</p>
                {data.device.mqttTopic && <code>{data.device.mqttTopic}</code>}
                {latestHealth && (
                  <div className="admin-entity-chips">
                    {latestHealth.batteryMv !== null && (
                      <span>
                        <IonIcon icon={batteryHalfOutline} />
                        {(latestHealth.batteryMv / 1000).toFixed(2)} V{latestHealth.batteryPct !== null ? ` · ${latestHealth.batteryPct}%` : ''}
                      </span>
                    )}
                    {latestHealth.rssi !== null && (
                      <span>
                        <IonIcon icon={cellularOutline} />
                        {latestHealth.rssi} dBm
                      </span>
                    )}
                    {latestHealth.firmware && (
                      <span>
                        <IonIcon icon={codeSlashOutline} />
                        v{latestHealth.firmware}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <IonCard className="admin-detail-card">
              <IonCardHeader>
                <IonCardTitle>
                  <span className="admin-card-icon tone-blue">
                    <IonIcon icon={gitNetworkOutline} />
                  </span>
                  Origen de datos
                </IonCardTitle>
                <IonCardSubtitle>
                  Topic en nuestro Mosquitto: el topic base del spot LoRa (de ahí cuelgan estado, info y sensores/...) o el topic exacto de
                  un equipo que manda un JSON con todo
                </IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <IonItem lines="full">
                  <IonInput
                    label="Topic"
                    labelPlacement="stacked"
                    placeholder="acme/hub-01/spot-01"
                    value={topic}
                    onIonInput={(e) => setTopic(e.detail.value ?? '')}
                  />
                </IonItem>
                <IonButton expand="block" style={{ marginTop: 12 }} onClick={handleLink} disabled={linking}>
                  {linking ? 'Guardando...' : 'Guardar topic'}
                </IonButton>
              </IonCardContent>
            </IonCard>

            <DeviceHealthCard health={data.health} />

            <IonCard className="admin-detail-card">
              <IonCardHeader>
                <IonCardTitle>
                  <span className="admin-card-icon tone-green">
                    <IonIcon icon={hardwareChipOutline} />
                  </span>
                  Lo que tiene la estación
                </IonCardTitle>
                <IonCardSubtitle>Lo que tildó el usuario o apareció solo con los datos, y si llega algo para cada uno</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                {data.components.length === 0 && <p className="admin-list-row-meta">Todavía no tiene componentes.</p>}
                {data.components.map((c) => {
                  const state = COMPONENT_STATE[c.status];
                  return (
                    <IonItem key={c.id} lines="full">
                      <IonIcon icon={COMPONENT_ICON[c.kind]} slot="start" color="medium" />
                      <IonLabel>
                        <strong>{c.displayName}</strong>
                        <p>
                          {c.source === 'manual' ? 'Tildado por el usuario' : 'Detectado por los datos'}
                          {!c.enabled && ' · apagado'}
                        </p>
                        <IonText color={state.color}>
                          <p>
                            {state.text}
                            {c.lastDataAt && (c.status === 'receiving' || c.status === 'stale') && ` · último ${formatRelative(c.lastDataAt)}`}
                          </p>
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonCardContent>
            </IonCard>

            {data.sensors.length === 0 && (
              <IonText color="medium">
                <p className="ion-padding">
                  Todavía no llegó ningún sensor de este spot. Aparecen solos acá cuando publican en{' '}
                  <code>{data.device.mqttTopic}/sensores/…</code>
                </p>
              </IonText>
            )}

            {data.sensors.length > 0 && (
              <p className="admin-list-row-meta ion-padding-horizontal">
                Todos los sensores que publicó este spot alguna vez, con su mapeo: quedan acá aunque dejen de mandar. Una métrica sale de un
                solo sensor; si dos miden lo mismo, el otro queda en «Ignorar».
              </p>
            )}

            {data.sensors.map((sensor) => {
              const mapping = mappingFor(sensor.id);
              const fields = Array.from(new Set([...sensor.fields, ...Object.keys(mapping)]));
              const dirty = !!drafts[sensor.id];
              return (
                <IonCard key={sensor.id} className="admin-detail-card">
                  <IonCardHeader>
                    <IonCardTitle>
                      <span className="admin-card-icon tone-orange">
                        <IonIcon icon={thermometerOutline} />
                      </span>
                      {sensor.name}
                    </IonCardTitle>
                    <IonCardSubtitle>
                      {sensor.lastSeenAt ? `Último mensaje ${formatRelative(sensor.lastSeenAt)}` : 'Sin datos'}
                      {Object.keys(sensor.mapping).length === 0 ? ' · sin mapear: no se guarda nada' : ''}
                    </IonCardSubtitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {sensor.lastPayload && <code className="admin-mqtt-payload">{JSON.stringify(sensor.lastPayload)}</code>}
                    {fields.length === 0 && <p className="admin-list-row-meta">El mensaje no trae campos numéricos para mapear.</p>}
                    {fields.map((field) => {
                      const rejected = sensor.rejected.find((r) => r.field === field);
                      return (
                        <IonItem key={field} lines="full">
                          <IonLabel>
                            <strong>{field}</strong>
                            <p>{sensor.lastPayload && field in sensor.lastPayload ? String(sensor.lastPayload[field]) : '—'}</p>
                            {rejected && (
                              <IonText color="danger">
                                <p>
                                  Fuera de rango ({rejected.min} a {rejected.max} {rejected.unit}): no se guarda
                                </p>
                              </IonText>
                            )}
                          </IonLabel>
                          <IonSelect
                            slot="end"
                            aria-label={`Métrica de ${field}`}
                            interface="popover"
                            value={toValue(mapping[field])}
                            onIonChange={(e) => setFieldTarget(sensor.id, field, fromValue(e.detail.value))}
                          >
                            <IonSelectOption value="">Ignorar</IonSelectOption>
                            {options.map((o) => (
                              <IonSelectOption key={o.value} value={o.value}>
                                {o.label}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                        </IonItem>
                      );
                    })}
                    {dirty && (
                      <IonButton
                        expand="block"
                        style={{ marginTop: 12 }}
                        disabled={savingSensor === sensor.id}
                        onClick={() => saveMapping(sensor.id)}
                      >
                        {savingSensor === sensor.id ? 'Guardando...' : 'Guardar mapeo'}
                      </IonButton>
                    )}
                  </IonCardContent>
                </IonCard>
              );
            })}
          </>
        )}

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2000} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
};

export default AdminDeviceMqtt;
